interface TrendPoint {
  date: string;
  value: number;
}

interface ProductTrendSparklineProps {
  points?: TrendPoint[];
  change7d?: number | null;
  change30d?: number | null;
  change90d?: number | null;
  compact?: boolean;
  weekly?: {
    series: (number | null)[];
    streakWeeks?: number;
    change4w: number | null;
    change8w: number | null;
    change12w: number | null;
    name: string;
  };
}

import { streakStartIndex } from "@/lib/early-warning";

const WIDTH = 300;
const HEIGHT = 58;
const PAD = 3;

function trendPath(points: TrendPoint[]): string {
  if (points.length < 2) return "";
  let min = Infinity;
  let max = -Infinity;
  for (const point of points) {
    min = Math.min(min, point.value);
    max = Math.max(max, point.value);
  }
  const span = Math.max(max - min, max * 0.025, 1);
  return points.map((point, index) => {
    const x = PAD + (index / (points.length - 1)) * (WIDTH - PAD * 2);
    const y = HEIGHT - PAD - ((point.value - min) / span) * (HEIGHT - PAD * 2);
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function Change({ label, value }: { label: string; value?: number | null }) {
  const available = typeof value === "number" && Number.isFinite(value);
  const tone = !available ? "text-(--color-muted)" : value > 0 ? "text-(--trend-up)" : value < 0 ? "text-(--trend-down)" : "text-(--color-muted)";
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-eyebrow font-bold uppercase tracking-[0.1em] text-(--color-muted)">{label}</span>
      <strong className={`font-(family-name:--font-mono) text-caption ${tone}`}>
        {available ? `${value > 0 ? "+" : ""}${value.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%` : "—"}
      </strong>
    </span>
  );
}

function WeeklyTrend({ weekly }: { weekly: NonNullable<ProductTrendSparklineProps["weekly"]> }) {
  const values = weekly.series;
  const finite = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = Math.max(max - min, max * 0.025, 1);
  const xy = (value: number, index: number) => ({ x: PAD + index * (WIDTH - PAD * 2) / Math.max(values.length - 1, 1), y: HEIGHT - PAD - (value - min) * (HEIGHT - PAD * 2) / span });
  const runs: { line: string; area: string }[] = [];
  let current: { x: number; y: number }[] = [];
  const flush = () => {
    if (current.length > 1) {
      const line = current.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
      runs.push({ line, area: `${line} L${current.at(-1)!.x.toFixed(1)},${HEIGHT - PAD} L${current[0].x.toFixed(1)},${HEIGHT - PAD} Z` });
    }
    current = [];
  };
  values.forEach((value, index) => value != null && Number.isFinite(value) ? current.push(xy(value, index)) : flush());
  flush();
  const start = streakStartIndex(values.length, weekly.streakWeeks ?? 0);
  const streak = start == null ? [] : values.slice(start).map((value, offset) => value == null ? null : xy(value, start + offset));
  const highlight = streak.length > 1 && streak.every((point) => point !== null) ? streak as { x: number; y: number }[] : [];
  const highlightLine = highlight.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const lastIndex = values.length - 1;
  const last = values[lastIndex];
  const lastPoint = last != null && Number.isFinite(last) ? xy(last, lastIndex) : null;
  return <div className="min-w-0">
    <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1"><span className="text-eyebrow text-(--color-muted)">12 haftalık seyir</span><Change label="4H" value={weekly.change4w} /><Change label="8H" value={weekly.change8w} /><Change label="12H" value={weekly.change12w} /></div>
    {runs.length ? <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className="h-16 w-full" role="img" aria-label={`${weekly.name}: son 12 haftalık toptan hal ortalaması. Koyu alan kesintisiz yükseliş dönemidir.`}>
      <path d={`M${PAD},${HEIGHT - PAD} H${WIDTH - PAD}`} stroke="var(--color-chart-grid)" strokeWidth="1" fill="none" />
      {runs.map((run, index) => <g key={index}><path d={run.area} fill="var(--trend-up)" opacity="0.08" /><path d={run.line} stroke="var(--trend-up)" strokeWidth="1.8" vectorEffect="non-scaling-stroke" fill="none" /></g>)}
      {highlight.length ? <><path d={`${highlightLine} L${highlight.at(-1)!.x.toFixed(1)},${HEIGHT - PAD} L${highlight[0].x.toFixed(1)},${HEIGHT - PAD} Z`} fill="var(--trend-up)" opacity="0.18" /><path d={highlightLine} stroke="var(--trend-up)" strokeWidth="2.8" vectorEffect="non-scaling-stroke" fill="none" /></> : null}
      {lastPoint ? <circle cx={lastPoint.x} cy={lastPoint.y} r="3" fill="var(--trend-up)" /> : null}
    </svg> : <p className="flex h-16 items-center text-caption text-(--color-muted)">Dönem grafiği için yeterli geçmiş yok</p>}
  </div>;
}

export default function ProductTrendSparkline({ points = [], change7d, change30d, change90d, compact = false, weekly }: ProductTrendSparklineProps) {
  const clean = points.filter((point) => Number.isFinite(point.value));
  const path = trendPath(clean);
  const first = clean[0]?.value;
  const last = clean.at(-1)?.value;
  const stroke = typeof first !== "number" || typeof last !== "number" || last === first
    ? "var(--color-muted)"
    : last > first ? "var(--trend-up)" : "var(--trend-down)";

  if (weekly) return <WeeklyTrend weekly={weekly} />;
  return (
    <div className={compact ? "mt-3 border-t border-(--color-border-soft) pt-3" : "min-w-0"}>
      <div className={compact ? "mb-1.5 grid gap-1" : "mb-1.5 flex items-center justify-between gap-3"}>
        <span className="font-(family-name:--font-mono) text-eyebrow font-bold uppercase tracking-[0.12em] text-(--color-muted)">90 günlük seyir</span>
        <div className={compact ? "flex items-center justify-between gap-2" : "flex gap-2.5"}>
          <Change label="7G" value={change7d} />
          <Change label="30G" value={change30d} />
          <Change label="90G" value={change90d} />
        </div>
      </div>
      {path ? (
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" className={`${compact ? "h-12" : "h-14"} w-full overflow-visible`} role="img" aria-label="Ürünün son 90 günlük orta fiyat seyri">
          <path d={`M${PAD},${HEIGHT - PAD} H${WIDTH - PAD}`} stroke="var(--border)" strokeWidth="1" strokeDasharray="3 4" fill="none" />
          <path d={`${path} L${WIDTH - PAD},${HEIGHT - PAD} L${PAD},${HEIGHT - PAD} Z`} fill={stroke} opacity="0.07" />
          <path d={path} stroke={stroke} strokeWidth="2.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          <circle cx={WIDTH - PAD} cy={path.match(/L([\d.]+),([\d.]+)$/)?.[2] ?? HEIGHT / 2} r="3" fill={stroke} />
        </svg>
      ) : (
        <div className={`${compact ? "h-12" : "h-14"} flex items-center border-y border-dashed border-(--color-border-soft) text-caption text-(--color-muted)`}>Dönem grafiği için yeterli geçmiş yok</div>
      )}
    </div>
  );
}
